// src/components/LogoWithStatus.jsx
import React, { useState, useEffect } from 'react';
import { statusMonitor } from '../utils/StatusMonitor';
import { STATUS_LEVELS } from '../utils/statusDefinitions';

export const LogoWithStatus = ({ src, alt = 'Logo', className = '', style = {} }) => {
  const [status, setStatus] = useState(statusMonitor.getStatus());
  const [metrics, setMetrics] = useState(statusMonitor.getMetrics());
  const [showTooltip, setShowTooltip] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    const unsubscribe = statusMonitor.subscribe((newStatus, newMetrics) => {
      setStatus(newStatus);
      setMetrics(newMetrics);
    });
    return unsubscribe;
  }, []);

  // Triple-click to show debug info
  const handleLogoClick = () => {
    setClickCount(prev => prev + 1);
    setTimeout(() => setClickCount(0), 1000);
    
    if (clickCount === 2) {
      setShowDebug(true);
    }
  };

  const statusInfo = STATUS_LEVELS[status];
  const hasIssue = statusInfo && !statusInfo.isHealthy;

  // Create a colored border or glow effect when there's an issue
  const enhancedStyle = {
    ...style,
    cursor: 'pointer',
    transition: 'filter 0.5s ease',
    ...(hasIssue && {
      border: `3px solid ${statusInfo.color}`,
      borderRadius: '8px',
      padding: '4px'
    })
  };

  return (
    <div 
      style={{ marginTop: '1.5em', marginLeft: '0.5em' }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <img 
        src={src}
        alt={alt}
        className={className}
        onClick={handleLogoClick}
        style={enhancedStyle}
      />

      {/* Status indicator dot */}
      {hasIssue && (
        <div 
          style={{
            position: 'absolute',
            top: '-4px',
            right: '-4px',
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: statusInfo.color,
            border: '3px solid white',
            boxShadow: '0 0 8px rgba(0,0,0,0.4)',
            animation: 'pulse 2s infinite',
            zIndex: 10
          }}
        />
      )}

      {/* Tooltip */}
      {/* {showTooltip && (
        <div 
          style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginTop: '12px',
            padding: '14px 18px',
            backgroundColor: 'rgba(0,0,0,0.92)',
            color: 'white',
            borderRadius: '8px',
            fontSize: '14px',
            whiteSpace: 'nowrap',
            zIndex: 1000,
            pointerEvents: 'none',
            boxShadow: '0 6px 12px rgba(0,0,0,0.4)',
            minWidth: '220px',
            maxWidth: '400px'
          }}
        >
          {hasIssue ? (
            <>
              <div style={{ 
                fontWeight: 'bold', 
                marginBottom: '8px', 
                color: statusInfo.color,
                fontSize: '15px'
              }}>
                {statusInfo.icon} {statusInfo.description}
              </div>
              <div style={{ 
                fontSize: '13px', 
                opacity: 0.9, 
                borderTop: '1px solid rgba(255,255,255,0.2)', 
                paddingTop: '8px', 
                marginTop: '8px' 
              }}>
                <div style={{ marginBottom: '4px' }}>
                  <strong>Unsynced:</strong> {metrics.unsyncedCount} records
                </div>
                {metrics.lastSuccessfulSync && (
                  <div style={{ marginBottom: '4px' }}>
                    <strong>Last sync:</strong> {new Date(metrics.lastSuccessfulSync).toLocaleTimeString()}
                  </div>
                )}
                {metrics.consecutiveFailures > 0 && (
                  <div style={{ color: '#FCA5A5', marginBottom: '4px' }}>
                    <strong>Failed attempts:</strong> {metrics.consecutiveFailures}
                  </div>
                )}
                <div style={{ marginTop: '4px', opacity: 0.7 }}>
                  <strong>Network:</strong> {navigator.onLine ? '✓ Online' : '✗ Offline'}
                </div>
              </div>
              <div style={{ 
                fontSize: '12px', 
                opacity: 0.7, 
                marginTop: '10px', 
                fontStyle: 'italic',
                textAlign: 'center'
              }}>
                Triple-click for troubleshooting guide
              </div>
            </>
          ) : (
            <>
              <div style={{ fontWeight: 'bold', color: '#10B981', fontSize: '15px' }}>
                ✓ System Healthy
              </div>
              {metrics.unsyncedCount > 0 && (
                <div style={{ fontSize: '13px', opacity: 0.8, marginTop: '8px' }}>
                  {metrics.unsyncedCount} records pending sync
                </div>
              )}
              {metrics.lastSuccessfulSync && (
                <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '6px' }}>
                  Last sync: {new Date(metrics.lastSuccessfulSync).toLocaleTimeString()}
                </div>
              )}
            </>
          )}
        </div>
      )} */}

      {/* Debug Modal */}
      {showDebug && <DebugModal onClose={() => setShowDebug(false)} status={status} metrics={metrics} />}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.15); }
        }
      `}</style>
    </div>
  );
};

// Status troubleshooting guide data
const STATUS_TROUBLESHOOTING = {

'backend-unreachable': {
  meaning: 'Cannot connect to backend server',
  causes: [
    'Backend server is down',
    'Backend server not responding',
    'Network blocking access to server',
    'Server firewall blocking requests',
    'Wrong API URL configured'
  ],
  solutions: [
    '🚨 URGENT - Backend server needs immediate attention',
    '1. Check if backend server is running',
    '2. Verify server process is active',
    '3. Check server logs for crashes',
    '4. Restart backend service',
    '5. Verify API URL in configuration',
    '6. Check firewall rules',
    '7. Contact backend administrator IMMEDIATELY'
  ]
},
'backend-down': {
  meaning: 'Backend server connection refused - server not running',
  causes: [
    'Backend server stopped/crashed',
    'Server process not started',
    'Server port not listening',
    'Server startup failed'
  ],
  solutions: [
    '🚨 CRITICAL - Backend server is NOT running',
    '1. Start backend server immediately',
    '2. Check server startup logs for errors',
    '3. Verify server configuration',
    '4. Check if port is already in use',
    '5. Restart server service',
    '6. Check system resources (CPU/memory)',
    '7. Contact backend administrator URGENTLY'
  ]
},
  'healthy': {
    meaning: 'All systems operational',
    causes: ['System working normally', 'All syncs successful', 'Network connected'],
    solutions: ['No action needed - continue normal operation']
  },
  'healthy-pending': {
    meaning: 'System healthy with records waiting to sync',
    causes: ['Normal operation with pending feedback', 'Less than 50 unsynced records'],
    solutions: ['No action needed - records will sync automatically']
  },
  'offline': {
    meaning: 'No internet connection',
    causes: [
      'WiFi disconnected on tablet',
      'Router or modem down',
      'ISP outage',
      'Network cable unplugged',
      'Building network issues'
    ],
    solutions: [
      '1. Check WiFi is enabled on tablet',
      '2. Verify WiFi network is available',
      '3. Check router/modem power and lights',
      '4. Try reconnecting to WiFi',
      '5. Restart router if needed',
      '6. Contact IT if building network is down'
    ]
  },
  'sync-delayed': {
    meaning: 'Sync hasn\'t completed in 5+ minutes',
    causes: [
      'Temporary network congestion',
      'Server briefly busy',
      'Large backlog being processed'
    ],
    solutions: [
      '1. Monitor for 10-15 minutes',
      '2. If persists, check backend server logs',
      '3. Verify network stability',
      '4. Check if other tablets have same issue'
    ]
  },
  'partial-sync': {
    meaning: 'Some records synced successfully, others failed',
    causes: [
      'Network hiccup during sync',
      'Some records have validation issues',
      'Backend processed some but not all'
    ],
    solutions: [
      '1. Review error history in this debug panel',
      '2. Check backend logs for specific errors',
      '3. Failed records will retry automatically',
      '4. If persists, contact technical support'
    ]
  },
  'high-backlog': {
    meaning: '50-200 unsynced records building up',
    causes: [
      'Sync process too slow',
      'Very busy location with lots of feedback',
      'Network intermittently dropping',
      'Backend server slow to respond'
    ],
    solutions: [
      '1. Check network connection stability',
      '2. Verify backend server performance',
      '3. Monitor if backlog continues growing',
      '4. May need to increase sync frequency',
      '5. Contact support if backlog exceeds 100'
    ]
  },
  'rds-timeout': {
    meaning: 'Database connection timing out',
    causes: [
      'AWS RDS instance slow to respond',
      'Network latency to database',
      'Database under heavy load',
      'Connection pool exhausted'
    ],
    solutions: [
      '1. Check AWS RDS dashboard status',
      '2. Verify database is running',
      '3. Check network latency to AWS',
      '4. Review backend connection pool settings',
      '5. Contact backend administrator'
    ]
  },
  'rds-error': {
    meaning: 'Cannot connect to database',
    causes: [
      'AWS RDS instance down or restarting',
      'Database credentials invalid',
      'Security group blocking connection',
      'Database server not responding'
    ],
    solutions: [
      '1. Check AWS RDS dashboard immediately',
      '2. Verify database instance is running',
      '3. Check security groups allow connection',
      '4. Review backend database credentials',
      '5. Restart backend service if needed',
      '6. Contact AWS/database administrator'
    ]
  },
  'backend-5xx': {
    meaning: 'Server error (HTTP 500-599)',
    causes: [
      'Backend server crashed',
      'Unhandled exception in server code',
      'Database query error',
      'Server out of memory or resources'
    ],
    solutions: [
      '1. Check backend server logs IMMEDIATELY',
      '2. Restart backend service',
      '3. Check server CPU and memory usage',
      '4. Review recent code deployments',
      '5. Fix bugs causing server errors',
      '6. Contact backend development team'
    ]
  },
  'backend-timeout': {
    meaning: 'Server taking too long to respond (>30 seconds)',
    causes: [
      'Long-running database queries',
      'Backend server overloaded',
      'Network latency issues',
      'Backend processing too slowly'
    ],
    solutions: [
      '1. Check backend CPU and memory usage',
      '2. Optimize slow database queries',
      '3. Review backend performance logs',
      '4. Scale up server resources if needed',
      '5. Add caching to reduce load',
      '6. Contact backend administrator'
    ]
  },
  'backend-overload': {
    meaning: 'Server overloaded (HTTP 503)',
    causes: [
      'Too many kiosks syncing simultaneously',
      'Backend server underpowered',
      'Unusual traffic spike',
      'Server resources exhausted'
    ],
    solutions: [
      '1. Scale backend horizontally (add servers)',
      '2. Add load balancer',
      '3. Stagger sync times across tablets',
      '4. Optimize backend performance',
      '5. Check for DDoS or unusual traffic',
      '6. Contact infrastructure team URGENTLY'
    ]
  },
  'client-4xx': {
    meaning: 'Client error (HTTP 400-499)',
    causes: [
      'Invalid data format being sent',
      'Missing required fields',
      'API endpoint changed',
      'Data validation failing'
    ],
    solutions: [
      '1. Check browser console for errors',
      '2. Review data being sent in network tab',
      '3. Verify API contract with backend',
      '4. Update frontend code if needed',
      '5. Check recent frontend deployments',
      '6. Contact frontend development team'
    ]
  },
  'auth-error': {
    meaning: 'Authentication failed (HTTP 401/403)',
    causes: [
      'Device not registered properly',
      'API tokens expired',
      'Invalid device_id',
      'Backend authentication changed',
      'Security policy blocking device'
    ],
    solutions: [
      '1. Check device_id in browser storage',
      '2. Re-register device if needed',
      '3. Refresh authentication tokens',
      '4. Verify backend auth configuration',
      '5. Check security policies',
      '6. Contact system administrator'
    ]
  },
  'data-validation-error': {
    meaning: 'Data validation failed (HTTP 422)',
    causes: [
      'Date/time format incorrect',
      'Invalid option selections',
      'Missing required fields',
      'Data constraints violated'
    ],
    solutions: [
      '1. Review specific validation errors in logs',
      '2. Check data format requirements',
      '3. Fix frontend validation to match backend',
      '4. Verify database constraints',
      '5. Update data validation rules',
      '6. Contact development team'
    ]
  },
  'indexeddb-error': {
    meaning: 'Local database error',
    causes: [
      'Browser storage quota exceeded',
      'IndexedDB corruption',
      'Browser cache issues',
      'Storage permissions blocked'
    ],
    solutions: [
      '1. Clear browser cache and storage',
      '2. Restart tablet',
      '3. Check available storage space',
      '4. Reset application data',
      '5. Reinstall application if needed',
      '6. Contact technical support'
    ]
  },
  'storage-full': {
    meaning: 'Local storage nearly full (80-95% used)',
    causes: [
      'Too many unsynced records',
      'Old synced records not cleaned up',
      'Large backlog never cleared',
      'Cleanup job not running'
    ],
    solutions: [
      '1. Force immediate sync',
      '2. Manually clear synced records',
      '3. Verify cleanup job is running',
      '4. Clear browser cache',
      '5. Check tablet storage space',
      '6. Restart application to trigger cleanup'
    ]
  },
  'critical-error': {
    meaning: 'Critical system error',
    causes: [
      'Unhandled JavaScript exception',
      'Unexpected system failure',
      'Multiple cascading errors',
      'System malfunction'
    ],
    solutions: [
      '1. Check browser console for errors',
      '2. Review all system logs',
      '3. Restart tablet',
      '4. Clear browser data',
      '5. Reinstall application if needed',
      '6. Contact technical support IMMEDIATELY'
    ]
  },
  'max-retries-exceeded': {
    meaning: 'Too many failed sync attempts (5+ consecutive)',
    causes: [
      'Persistent network issue',
      'Backend consistently failing',
      'Configuration problem',
      'Chronic system issue'
    ],
    solutions: [
      '1. Fix underlying network or backend issue first',
      '2. Check error history for root cause',
      '3. Restart application to reset retry counter',
      '4. Verify all system components operational',
      '5. Manual intervention required',
      '6. Contact support team'
    ]
  },
  'sync-stopped': {
    meaning: 'Sync completely stopped (15+ minutes no sync)',
    causes: [
      'Sync scheduler crashed',
      'Backend completely down',
      'Network completely unavailable',
      'Application needs restart'
    ],
    solutions: [
      '🚨 URGENT - IMMEDIATE ACTION REQUIRED',
      '1. Check if backend server is running',
      '2. Verify network connectivity',
      '3. Restart application IMMEDIATELY',
      '4. Check all system components',
      '5. Review critical error logs',
      '6. Contact support team IMMEDIATELY'
    ]
  },
  'slow-connection': {
    meaning: 'Slow network detected (>3 seconds response)',
    causes: [
      'Network congestion',
      'Poor WiFi signal',
      'ISP throttling',
      'Router overloaded'
    ],
    solutions: [
      '1. Check WiFi signal strength',
      '2. Move tablet closer to router',
      '3. Restart router',
      '4. Check for network congestion',
      '5. Contact ISP if persists',
      '6. Consider upgrading internet plan'
    ]
  }
};

// Priority levels for UI display
const getPriorityLevel = (status) => {
  const priorities = {
    'sync-stopped': { level: 'CRITICAL', color: '#DC2626', icon: '🚨' },
    'max-retries-exceeded': { level: 'CRITICAL', color: '#DC2626', icon: '🚨' },
    'backend-overload': { level: 'HIGH', color: '#EA580C', icon: '⚠️' },
    'backend-5xx': { level: 'HIGH', color: '#EA580C', icon: '⚠️' },
    'rds-error': { level: 'HIGH', color: '#EA580C', icon: '⚠️' },
    'offline': { level: 'HIGH', color: '#EA580C', icon: '⚠️' },
    'critical-error': { level: 'HIGH', color: '#EA580C', icon: '⚠️' },
    'indexeddb-error': { level: 'MEDIUM', color: '#F59E0B', icon: '⚠' },
    'storage-full': { level: 'MEDIUM', color: '#F59E0B', icon: '⚠' },
    'high-backlog': { level: 'MEDIUM', color: '#F59E0B', icon: '⚠' },
    'backend-timeout': { level: 'MEDIUM', color: '#F59E0B', icon: '⚠' },
    'rds-timeout': { level: 'MEDIUM', color: '#F59E0B', icon: '⚠' },
    'auth-error': { level: 'MEDIUM', color: '#F59E0B', icon: '⚠' },
    'partial-sync': { level: 'LOW', color: '#3B82F6', icon: 'ℹ️' },
    'sync-delayed': { level: 'LOW', color: '#3B82F6', icon: 'ℹ️' },
    'slow-connection': { level: 'LOW', color: '#3B82F6', icon: 'ℹ️' },
    'client-4xx': { level: 'LOW', color: '#3B82F6', icon: 'ℹ️' },
    'data-validation-error': { level: 'LOW', color: '#3B82F6', icon: 'ℹ️' }
  };
  
  return priorities[status] || { level: 'INFO', color: '#10B981', icon: '✓' };
};

// Debug Modal Component
const DebugModal = ({ onClose, status, metrics }) => {
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'troubleshooting', 'history', 'device'

  useEffect(() => {
    statusMonitor.getHistory().then(setHistory);
  }, []);

  const statusInfo = STATUS_LEVELS[status];
  const troubleshooting = STATUS_TROUBLESHOOTING[status] || STATUS_TROUBLESHOOTING['healthy'];
  const priority = getPriorityLevel(status);

  const tabStyle = (tabName) => ({
    padding: '12px 24px',
    backgroundColor: activeTab === tabName ? '#3B82F6' : '#F3F4F6',
    color: activeTab === tabName ? 'white' : '#374151',
    border: 'none',
    borderRadius: '8px 8px 0 0',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    marginRight: '4px',
    transition: 'all 0.2s'
  });

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.88)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        overflowY: 'auto'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '0',
          maxWidth: '900px',
          maxHeight: '90vh',
          overflow: 'hidden',
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '24px 32px',
          borderBottom: '2px solid #E5E7EB'
        }}>
          <h2 style={{ margin: 0, fontSize: '26px', fontWeight: 'bold' }}>
            System Debug Information
          </h2>
          <button 
            onClick={onClose}
            style={{
              padding: '10px 16px',
              backgroundColor: '#EF4444',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            Close
          </button>
        </div>

        {/* Tabs */}
        <div style={{ 
          padding: '0 32px',
          backgroundColor: '#F9FAFB',
          borderBottom: '1px solid #E5E7EB'
        }}>
          <button style={tabStyle('overview')} onClick={() => setActiveTab('overview')}>
            Overview
          </button>
          <button style={tabStyle('troubleshooting')} onClick={() => setActiveTab('troubleshooting')}>
            Troubleshooting
          </button>
          <button style={tabStyle('history')} onClick={() => setActiveTab('history')}>
            History
          </button>
          <button style={tabStyle('device')} onClick={() => setActiveTab('device')}>
            Device Info
          </button>
        </div>

        {/* Tab Content */}
        <div style={{ 
          padding: '28px 32px',
          overflowY: 'auto',
          flex: 1
        }}>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <>
              {/* Current Status */}
              <div style={{ 
                marginBottom: '28px',
                padding: '20px',
                backgroundColor: statusInfo?.isHealthy ? '#ECFDF5' : '#FEF2F2',
                borderRadius: '10px',
                borderLeft: `5px solid ${statusInfo?.color || '#10B981'}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                    Current Status
                  </h3>
                  <span style={{
                    padding: '6px 12px',
                    backgroundColor: priority.color,
                    color: 'white',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '700'
                  }}>
                    {priority.icon} {priority.level} PRIORITY
                  </span>
                </div>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '16px', 
                  fontSize: '14px' 
                }}>
                  <div>
                    <strong>Status:</strong>
                    <div style={{ 
                      marginTop: '6px', 
                      color: statusInfo?.color || '#10B981', 
                      fontWeight: 'bold',
                      fontSize: '15px'
                    }}>
                      {statusInfo?.icon} {statusInfo?.description}
                    </div>
                  </div>
                  <div>
                    <strong>Unsynced Records:</strong>
                    <div style={{ 
                      marginTop: '6px',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: metrics.unsyncedCount > 50 ? '#EF4444' : '#10B981'
                    }}>
                      {metrics.unsyncedCount}
                    </div>
                  </div>
                  <div>
                    <strong>Consecutive Failures:</strong>
                    <div style={{ 
                      marginTop: '6px',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: metrics.consecutiveFailures > 0 ? '#EF4444' : '#10B981' 
                    }}>
                      {metrics.consecutiveFailures}
                    </div>
                  </div>
                  <div>
                    <strong>Network Status:</strong>
                    <div style={{ marginTop: '6px', fontSize: '15px' }}>
                      {navigator.onLine ? '✓ Online' : '✗ Offline'}
                    </div>
                  </div>
                  <div>
                    <strong>Last Sync Attempt:</strong>
                    <div style={{ marginTop: '6px', fontSize: '13px' }}>
                      {metrics.lastSyncAttempt ? new Date(metrics.lastSyncAttempt).toLocaleString() : 'Never'}
                    </div>
                  </div>
                  <div>
                    <strong>Last Successful Sync:</strong>
                    <div style={{ marginTop: '6px', fontSize: '13px' }}>
                      {metrics.lastSuccessfulSync ? new Date(metrics.lastSuccessfulSync).toLocaleString() : 'Never'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Summary */}
              <div style={{
                padding: '20px',
                backgroundColor: '#F0F9FF',
                borderRadius: '10px',
                borderLeft: '5px solid #3B82F6'
              }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>
                  💡 Quick Summary
                </h4>
                <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}>
                  {troubleshooting.meaning}
                </p>
              </div>
            </>
          )}

          {/* Troubleshooting Tab */}
          {activeTab === 'troubleshooting' && (
            <>
              <div style={{
                padding: '20px',
                backgroundColor: priority.level === 'CRITICAL' ? '#FEF2F2' : priority.level === 'HIGH' ? '#FFF7ED' : '#F0F9FF',
                borderRadius: '10px',
                borderLeft: `5px solid ${priority.color}`,
                marginBottom: '24px'
              }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '20px', fontWeight: '600', color: priority.color }}>
                  {priority.icon} {statusInfo?.description}
                </h3>
                <p style={{ margin: 0, fontSize: '15px', fontWeight: '500' }}>
                  {troubleshooting.meaning}
                </p>
              </div>

              {/* Possible Causes */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#374151' }}>
                  🔍 Possible Causes
                </h4>
                <ul style={{ 
                  margin: 0, 
                  padding: '0 0 0 24px',
                  fontSize: '14px',
                  lineHeight: '1.8'
                }}>
                  {troubleshooting.causes.map((cause, idx) => (
                    <li key={idx} style={{ marginBottom: '8px' }}>{cause}</li>
                  ))}
                </ul>
              </div>

              {/* Solutions */}
              <div style={{
                padding: '20px',
                backgroundColor: '#F0FDF4',
                borderRadius: '10px',
                borderLeft: '5px solid #10B981'
              }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#10B981' }}>
                  ✅ Recommended Solutions
                </h4>
                <ol style={{ 
                  margin: 0, 
                  padding: '0 0 0 24px',
                  fontSize: '14px',
                  lineHeight: '1.8'
                }}>
                  {troubleshooting.solutions.map((solution, idx) => (
                    <li key={idx} style={{ 
                      marginBottom: '12px',
                      fontWeight: solution.includes('URGENT') || solution.includes('IMMEDIATELY') ? '700' : '400',
                      color: solution.includes('URGENT') || solution.includes('IMMEDIATELY') ? '#DC2626' : '#374151'
                    }}>
                      {solution}
                    </li>
                  ))}
                </ol>
              </div>

              {/* Contact Support Box */}
              {(priority.level === 'CRITICAL' || priority.level === 'HIGH') && (
                <div style={{
                  marginTop: '24px',
                  padding: '16px',
                  backgroundColor: '#FEF2F2',
                  borderRadius: '8px',
                  border: '2px solid #FCA5A5',
                  textAlign: 'center'
                }}>
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#DC2626' }}>
                    ⚠️ {priority.level === 'CRITICAL' ? 'IMMEDIATE' : 'URGENT'} Support Required
                  </p>
                  <p style={{ margin: 0, fontSize: '13px', color: '#7F1D1D' }}>
                    Contact technical support if issue persists after following solutions above
                  </p>
                </div>
              )}
            </>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: '600' }}>
                Recent Status Changes
              </h3>
              <div style={{ 
                maxHeight: '500px', 
                overflow: 'auto', 
                fontSize: '13px',
                border: '1px solid #E5E7EB',
                borderRadius: '10px'
              }}>
                {history.length === 0 ? (
                  <div style={{ 
                    padding: '24px', 
                    textAlign: 'center', 
                    color: '#6B7280',
                    fontSize: '14px'
                  }}>
                    No history available
                  </div>
                ) : (
                  history.slice(-20).reverse().map((entry, idx) => (
                    <div 
                      key={idx} 
                      style={{ 
                        padding: '14px 18px', 
                        borderBottom: idx < Math.min(history.length, 20) - 1 ? '1px solid #E5E7EB' : 'none',
                        backgroundColor: idx % 2 === 0 ? 'white' : '#F9FAFB'
                      }}
                    >
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        marginBottom: '6px',
                        alignItems: 'center'
                      }}>
                        <span style={{ fontWeight: '600', fontSize: '14px' }}>
                          {STATUS_LEVELS[entry.from]?.icon} {entry.from} → {STATUS_LEVELS[entry.to]?.icon} {entry.to}
                        </span>
                        <span style={{ 
                          color: '#6B7280', 
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {new Date(entry.timestamp).toLocaleString()}
                        </span>
                      </div>
                      {entry.metadata && Object.keys(entry.metadata).filter(k => k !== 'metrics').length > 0 && (
                        <details style={{ marginTop: '8px' }}>
                          <summary style={{ 
                            cursor: 'pointer', 
                            color: '#6B7280', 
                            fontSize: '12px',
                            fontWeight: '500'
                          }}>
                            Show details
                          </summary>
                          <pre style={{ 
                            fontSize: '11px', 
                            color: '#374151',
                            backgroundColor: '#F3F4F6',
                            padding: '10px',
                            borderRadius: '6px',
                            marginTop: '8px',
                            overflow: 'auto',
                            maxHeight: '150px'
                          }}>
                            {JSON.stringify(entry.metadata, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Device Info Tab */}
          {activeTab === 'device' && (
            <>
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#F9FAFB', 
                borderRadius: '10px', 
                marginBottom: '20px'
              }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: '600' }}>
                  Device Information
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
                  <div>
                    <strong>Device ID:</strong>
                    <div style={{ marginTop: '4px', fontFamily: 'monospace', fontSize: '12px' }}>
                      {localStorage.getItem('device_id') || 'Not set'}
                    </div>
                  </div>
                  <div>
                    <strong>Device Type:</strong>
                    <div style={{ marginTop: '4px' }}>
                      {localStorage.getItem('device_type') || 'Not set'}
                    </div>
                  </div>
                  <div>
                    <strong>Platform:</strong>
                    <div style={{ marginTop: '4px' }}>{navigator.platform}</div>
                  </div>
                  <div>
                    <strong>Online Status:</strong>
                    <div style={{ marginTop: '4px', fontWeight: '600', color: navigator.onLine ? '#10B981' : '#EF4444' }}>
                      {navigator.onLine ? '✓ Online' : '✗ Offline'}
                    </div>
                  </div>
                  <div>
                    <strong>Language:</strong>
                    <div style={{ marginTop: '4px' }}>{navigator.language}</div>
                  </div>
                  <div>
                    <strong>Cookies Enabled:</strong>
                    <div style={{ marginTop: '4px' }}>
                      {navigator.cookieEnabled ? 'Yes' : 'No'}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ 
                padding: '20px', 
                backgroundColor: '#F9FAFB', 
                borderRadius: '10px'
              }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>
                  User Agent
                </h4>
                <div style={{ 
                  fontSize: '12px', 
                  wordBreak: 'break-all',
                  fontFamily: 'monospace',
                  backgroundColor: 'white',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #E5E7EB'
                }}>
                  {navigator.userAgent}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LogoWithStatus;